import { DatePipe } from "@angular/common";
import { Component, effect, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NotificationService } from "@kno2/common/services/notifications";
import { APP_CONFIG_TOKEN } from "@kno2/core/config";
import { SessionService } from "@kno2/core/session";
import { IdProofingService } from "@kno2/data-access/id-proofing";
import { DialogBodyComponent, DialogFooterComponent, DialogService } from "@kno2/shared/dialog";
import { NotificationAlertComponent } from "@kno2/shared/notification-alert";

@Component({
    selector: "kno2-onboarding-id-proofing-confirmation-modal",
    imports: [DialogBodyComponent, DialogFooterComponent, FormsModule, DatePipe, NotificationAlertComponent],
    templateUrl: "./onboarding-id-proofing-confirmation-modal.component.html"
})
export class OnboardingIdProofingConfirmationModalComponent {
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly dialogService = inject(DialogService);
    private readonly sessionService = inject(SessionService);
    private readonly notificationService = inject(NotificationService);
    private readonly idProofingService = inject(IdProofingService);

    protected readonly error = signal<string | null>(null);
    protected readonly isLoading = signal<boolean>(true);
    protected readonly brandDisplayName = signal(this.appConfig.brandDisplayName);
    protected readonly isConfirmed = signal(false);
    protected readonly isSubmitting = signal<boolean>(false);
    protected readonly verificationSession = this.idProofingService.getVerificationSession();

    protected getVerificationSessionCompleteEffect = effect(() => {
        try {
            const verificationSession = this.verificationSession();
            if (verificationSession) {
                this.isLoading.set(false);
                this.getVerificationSessionCompleteEffect.destroy();
            }
        } catch {
            this.isLoading.set(false);
            this.error.set("There was an error while retrieving your information from CLEAR.");
        }
    });

    protected async confirm(): Promise<void> {
        try {
            this.isSubmitting.set(true);

            await this.idProofingService.confirmAsync();
            await this.sessionService.refreshProfile();

            this.notificationService.success("The identity verification process completed successfully.");

            this.dialogService.close(true);
        } catch {
            this.error.set("An error has occurred while confirming your data.");
        } finally {
            this.isSubmitting.set(false);
        }
    }
}
